import { useEffect, useLayoutEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ApprovalDialog from "@/components/ApprovalDialog";
import PermissionTree from "@/components/PermissionTree";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Save, Lock, Plus, Trash2, Sparkles, GitBranch, Power, Percent, ChevronDown, Pencil, Eye, Inbox } from "lucide-react";

export default function Roles() {
  const { user: me, feature } = useAuth();
  const isAdmin = me?.role === "admin";
  const [perms, setPerms] = useState([]);
  const [matrix, setMatrix] = useState([]);
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(null); // fn(cred) awaiting approval

  const [metaLabel, setMetaLabel] = useState("");
  const [moduleCatalog, setModuleCatalog] = useState([]);
  const [moduleHidden, setModuleHidden] = useState(new Set());
  const [permQuery, setPermQuery] = useState("");
  const [editorTab, setEditorTab] = useState("perms");
  const [capOpen, setCapOpen] = useState(false);
  const [tree, setTree] = useState(null);
  const [treeQuery, setTreeQuery] = useState("");
  const [savedTick, setSavedTick] = useState(0);
  const [metaOpen, setMetaOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newSup, setNewSup] = useState("admin");
  const [newTemplate, setNewTemplate] = useState("");
  const [templates, setTemplates] = useState([]);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const [p, m] = await Promise.all([api.get("/permissions"), api.get("/role-permissions")]);
    setPerms(p.data.permissions);
    setModuleCatalog(p.data.modules || []);
    setMatrix(m.data);
    return m.data;
  };

  useEffect(() => {
    api.get("/permission-tree").then((r) => setTree(r.data)).catch(() => {});
    api.get("/role-templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {});
    load().then((m) => {
      const first = m.find((r) => r.editable) || m[0];
      if (first) selectRole(first);
    });
    // eslint-disable-next-line
  }, []);

  const activeRole = matrix.find((r) => r.role === active);
  const isAdminRole = active === "admin";
  const isCustom = activeRole && !activeRole.builtin;

  const selectRole = (r) => {
    setActive(r.role);
    setEditorTab("perms");
    setDraft(new Set(r.permissions));
    setModuleHidden(new Set(r.hidden_modules || []));
    setMetaLabel(r.label);
  };
  const doToggleActive = async (makeActive) => {
    setSaving(true);
    try {
      await api.put(`/roles/${active}/active`, { active: makeActive });
      toast.success(makeActive ? "Rol aktifleştirildi" : "Rol pasifleştirildi (atama listelerinden gizlendi)");
      const m = await load();
      const cur = m.find((r) => r.role === active);
      if (cur) selectRole(cur);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const toggle = (code) => setDraft((d) => { const n = new Set(d); n.has(code) ? n.delete(code) : n.add(code); return n; });
  const toggleModule = (key) => setModuleHidden((d) => { const n = new Set(d); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const toggleGroupPerms = (items, on) => setDraft((d) => { const n = new Set(d); items.forEach((p) => (on ? n.add(p.code) : n.delete(p.code))); return n; });

  const groups = perms.reduce((acc, p) => { (acc[p.group] = acc[p.group] || []).push(p); return acc; }, {});
  const moduleGroups = moduleCatalog.reduce((acc, m) => { (acc[m.group] = acc[m.group] || []).push(m); return acc; }, {});
  const _pq = permQuery.trim().toLocaleLowerCase("tr");
  const filteredGroups = Object.entries(groups)
    .map(([g, items]) => [g, _pq ? items.filter((p) => (p.label || "").toLocaleLowerCase("tr").includes(_pq) || p.code.toLowerCase().includes(_pq)) : items])
    .filter(([, items]) => items.length);

  const run = (fn) => { isAdmin ? fn(null) : setPending(() => fn); };

  const reloadActive = async () => { const m = await load(); const r = m.find((x) => x.role === active); if (r) selectRole(r); };

  // Onay Zinciri Haritası'ndaki düğümden amir düzenleme (admin doğrudan, diğerleri onaylı).
  const saveSuperiorsFor = (role, supsArray) => {
    const doIt = async (approver) => {
      try {
        const body = { superiors: supsArray };
        if (approver) body.approver = approver;
        await api.put(`/role-superiors/${role}`, body);
        toast.success("Amir (onay zinciri) güncellendi.");
        const m = await load();
        const r = m.find((x) => x.role === active); if (r) selectRole(r);
      } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };
    isAdmin ? doIt(null) : setPending(() => doIt);
  };

  // Onay Zinciri Haritası'ndan "+ Alt rütbe" — yeni rol oluştur, amiri tıklanan düğüm olsun.
  const addSubordinateOf = (role) => { setNewLabel(""); setNewSup(role); setNewTemplate(""); setCreateOpen(true); };

  // --- Otomatik kayıt (Kaydet butonu yok) ---
  const flashSaved = () => setSavedTick(Date.now());
  const savePermsAuto = async (nextSet, approver) => {
    const prev = new Set(draft);
    setDraft(new Set(nextSet));
    try {
      const body = { permissions: Array.from(nextSet) };
      if (approver) body.approver = approver;
      await api.put(`/role-permissions/${active}`, body);
      flashSaved();
    } catch (err) { setDraft(prev); toast.error(apiError(err.response?.data?.detail) || "Kaydedilemedi"); }
  };
  const togglePermAuto = (code) => {
    const next = new Set(draft); next.has(code) ? next.delete(code) : next.add(code);
    isAdmin ? savePermsAuto(next) : setPending(() => (cred) => savePermsAuto(next, cred));
  };
  const toggleAllModulePerms = (m) => {
    const codes = (m.perms || []).map((p) => p.code);
    if (!codes.length) return;
    const allOn = codes.every((c) => draft.has(c));
    const next = new Set(draft);
    codes.forEach((c) => (allOn ? next.delete(c) : next.add(c)));
    isAdmin ? savePermsAuto(next) : setPending(() => (cred) => savePermsAuto(next, cred));
  };
  const saveVisAuto = async (nextHidden) => {
    const prev = new Set(moduleHidden);
    setModuleHidden(new Set(nextHidden));
    try { await api.put(`/role-module-visibility/${active}`, { hidden: Array.from(nextHidden) }); flashSaved(); }
    catch (err) { setModuleHidden(prev); toast.error(apiError(err.response?.data?.detail) || "Kaydedilemedi (yalnızca Yönetici)"); }
  };
  const toggleVisAuto = (key) => {
    const next = new Set(moduleHidden); next.has(key) ? next.delete(key) : next.add(key);
    saveVisAuto(next);
  };

  const doSavePerms = async (approver) => {
    setSaving(true);
    try {
      const body = { permissions: Array.from(draft) };
      if (approver) body.approver = approver;
      await api.put(`/role-permissions/${active}`, body);
      toast.success("Rol yetkileri güncellendi");
      await reloadActive();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doSaveMeta = async (approver) => {
    if (!metaLabel.trim()) return toast.error("Rol adı zorunlu");
    setSaving(true);
    try {
      const body = { label: metaLabel.trim() };
      if (approver) body.approver = approver;
      await api.put(`/roles/${active}`, body);
      toast.success("Rol adı güncellendi");
      await reloadActive();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doSaveModules = async () => {
    setSaving(true);
    try {
      await api.put(`/role-module-visibility/${active}`, { hidden: Array.from(moduleHidden) });
      toast.success("Modül görünürlüğü kaydedildi");
      await reloadActive();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Kaydedilemedi (yalnızca Yönetici)"); }
    finally { setSaving(false); }
  };

  const doDelete = async (approver) => {
    try {
      await api.delete(`/roles/${active}`, { params: approver || {} });
      toast.success("Rol silindi");
      const m = await load(); const first = m.find((r) => r.editable) || m[0]; if (first) selectRole(first);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const askDelete = () => {
    if ((activeRole?.subordinates || []).length) return toast.error("Önce bu role bağlı alt rütbelerin amirini değiştirin.");
    if (!window.confirm(`"${activeRole?.label}" rolü silinsin mi? (Bu rolde kullanıcı olmamalı)`)) return;
    run(doDelete);
  };

  const doSaveTemplate = async () => {
    const name = window.prompt("Şablon adı:", metaLabel ? `${metaLabel} şablonu` : "Yeni şablon");
    if (!name || !name.trim()) return;
    try {
      await api.post("/role-templates", { name: name.trim(), permissions: Array.from(draft) });
      const r = await api.get("/role-templates"); setTemplates(r.data.templates || []);
      toast.success("Şablon kaydedildi — Yeni Rol açarken kullanabilirsin.");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Kaydedilemedi"); }
  };

  const doCreate = async (approver) => {
    if (!newLabel.trim()) return toast.error("Rol adı zorunlu");
    setCreating(true);
    try {
      let templatePerms = [];
      if (newTemplate.startsWith("tpl:")) {
        const t = templates.find((x) => x.id === newTemplate.slice(4));
        templatePerms = (t?.permissions || []).filter((c) => c !== "*");
      } else if (newTemplate) {
        const src = matrix.find((x) => x.role === newTemplate);
        templatePerms = (src?.permissions || []).filter((c) => c !== "*");
      }
      const body = { label: newLabel.trim(), superior_role: newSup, permissions: templatePerms };
      if (approver) body.approver = approver;
      const { data } = await api.post("/roles", body);
      toast.success(`"${data.label}" rolü oluşturuldu${newTemplate ? " (şablon yetkileriyle)" : ""}`);
      setCreateOpen(false); setNewLabel(""); setNewSup("admin"); setNewTemplate("");
      const m = await load(); const r = m.find((x) => x.role === data.code); if (r) selectRole(r);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  return (
    <div className="space-y-4 fade-in">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-brand-green" />
          <div><p className="overline">Yönetim</p><h1 className="font-heading text-2xl font-bold text-slate-900">Roller & Yetkiler</h1></div>
        </div>
        <Button data-testid="role-new-btn" onClick={() => { setNewLabel(""); setNewSup("admin"); setNewTemplate(""); setCreateOpen(true); }}
          className="bg-brand-green text-white hover:bg-brand-green-600">
          <Plus className="mr-1.5 h-4 w-4" /> Yeni Rol
        </Button>
      </div>

      <ChainMap matrix={matrix} isAdmin={isAdmin} perms={perms} onEditSuperiors={saveSuperiorsFor} onAddSubordinate={addSubordinateOf} onSelectRole={selectRole} />
      {feature("cap.discount", true) && <UserDiscountGrantsCard isAdmin={isAdmin} />}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        {/* Roles list */}
        <div className="rounded-md bg-white p-2 ring-1 ring-inset ring-slate-200">
          {matrix.map((r) => (
            <button key={r.role} data-testid={`role-tab-${r.role}`} onClick={() => selectRole(r)}
              className={`mb-1 flex w-full items-center justify-between rounded-md px-3 py-2.5 text-left text-sm font-medium transition-colors ${active === r.role ? "bg-brand-green text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <span className="flex items-center gap-1.5">
                {!r.builtin && <Sparkles className={`h-3 w-3 ${active === r.role ? "text-emerald-100" : "text-brand-orange"}`} />}
                {r.label}
                {r.disabled && <span className="ml-1 rounded-sm bg-amber-100 px-1 py-0.5 text-[9px] font-bold text-amber-700">PASİF</span>}
              </span>
              {!r.editable && <Lock className={`h-3.5 w-3.5 ${active === r.role ? "text-emerald-100" : "text-slate-300"}`} />}
            </button>
          ))}
        </div>

        {/* Editor */}
        <div className="lg:col-span-3 space-y-4">
          {!isAdminRole && activeRole && (
            <div className="rounded-md bg-white p-3 ring-1 ring-inset ring-slate-200" data-testid="role-editor-head">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-heading text-sm font-bold text-slate-800">{activeRole.label}</p>
                  <p className="text-[11px] text-slate-400">{draft.size} yetki · {tree ? (tree.module_keys.length - Array.from(moduleHidden).filter((k) => !k.includes("#")).length) : "—"} modül menüde</p>
                </div>
                <span className="text-[11px] font-semibold text-emerald-600" data-testid="role-autosave">
                  {savedTick ? <span key={savedTick} className="fade-in">✓ Otomatik kaydedildi</span> : "Otomatik kayıt açık"}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Input data-testid="perm-search" value={treeQuery} onChange={(e) => setTreeQuery(e.target.value)} placeholder="Modül / yetki / sekme ara…" className="h-8 flex-1 min-w-[150px] max-w-xs text-[13px]" />
                <button data-testid="role-cap-toggle" onClick={() => setCapOpen((o) => !o)} className={`rounded-md px-2.5 py-1.5 text-[11px] font-semibold ring-1 ring-inset transition-colors ${capOpen ? "bg-emerald-500 text-white ring-emerald-500" : "text-emerald-700 ring-emerald-200 hover:bg-emerald-50"}`}>Özet</button>
                {(isCustom || isAdmin) && <button data-testid="role-meta-toggle" onClick={() => setMetaOpen((o) => !o)} className={`rounded-md px-2.5 py-1.5 text-[11px] font-semibold ring-1 ring-inset transition-colors ${metaOpen ? "bg-slate-700 text-white ring-slate-700" : "text-slate-600 ring-slate-200 hover:bg-slate-50"}`}>Rol Ayarları</button>}
                {isAdmin && <button data-testid="role-save-template" onClick={doSaveTemplate} className="rounded-md px-2.5 py-1.5 text-[11px] font-semibold text-brand-orange ring-1 ring-inset ring-brand-orange/30 transition-colors hover:bg-orange-50">Şablon Kaydet</button>}
              </div>
            </div>
          )}
          {!isAdminRole && activeRole && tree && (() => {
            const roleAll = draft.has("*");
            const visibleScreens = tree.groups.flatMap((g) => g.modules).filter((m) => !moduleHidden.has(m.key) && (roleAll || (m.perms || []).length === 0 || (m.perms || []).some((p) => draft.has(p.code))));
            const ACTION_RE = /(write|manage|create|delete|adjust|approve|cancel|issue|manual|count|bulk|campaign|market|waste|entry|invoice|payroll|discount|wholesale)/;
            const editable = perms.filter((p) => draft.has(p.code) && ACTION_RE.test(p.code));
            return (
              <div data-testid="role-access-preview" className="rounded-md bg-white ring-1 ring-inset ring-slate-200">
                <button type="button" data-testid="role-access-preview-toggle" onClick={() => setPreviewOpen((o) => !o)} className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left">
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="flex items-center gap-1.5 font-heading text-[13px] font-bold text-slate-800"><Eye className="h-4 w-4 text-brand-green" /> Erişim Önizleme</span>
                    <span className="text-[11px] font-medium text-slate-400"><b className="text-sky-600">{visibleScreens.length}</b> ekran görür · <b className="text-emerald-600">{editable.length}</b> işlem yapar</span>
                  </span>
                  <span className="shrink-0 text-[11px] font-semibold text-brand-green">{previewOpen ? "Gizle" : "Detay"}</span>
                </button>
                {previewOpen && (
                  <div className="grid grid-cols-1 items-start gap-3 border-t border-slate-100 p-3 sm:grid-cols-2">
                    <div className="rounded-md bg-sky-50/40 p-2.5 ring-1 ring-inset ring-sky-100">
                      <p className="flex items-center gap-1 text-[11px] font-semibold text-sky-700"><Eye className="h-3.5 w-3.5" /> Gördüğü ekranlar ({visibleScreens.length})</p>
                      <div className="mt-1.5 flex max-h-40 flex-wrap content-start gap-1 overflow-y-auto pr-1" data-testid="preview-screens">
                        {visibleScreens.length ? visibleScreens.map((m) => <span key={m.key} className="h-fit rounded-full bg-white px-2 py-0.5 text-[10px] font-medium text-sky-700 ring-1 ring-inset ring-sky-200">{m.label}</span>) : <span className="text-[11px] text-slate-400">Menüde ekran yok — yalnızca giriş yapabilir.</span>}
                      </div>
                    </div>
                    <div className="rounded-md bg-emerald-50/40 p-2.5 ring-1 ring-inset ring-emerald-100">
                      <p className="flex items-center gap-1 text-[11px] font-semibold text-emerald-700"><Pencil className="h-3.5 w-3.5" /> Değiştirebildikleri ({editable.length})</p>
                      <div className="mt-1.5 flex max-h-40 flex-wrap content-start gap-1 overflow-y-auto pr-1" data-testid="preview-actions">
                        {editable.length ? editable.map((p) => <span key={p.code} className="h-fit rounded-full bg-white px-2 py-0.5 text-[10px] font-medium text-emerald-800 ring-1 ring-inset ring-emerald-200">{p.label}</span>) : <span className="text-[11px] text-slate-400">Yalnızca görüntüleme — değişiklik yapamaz.</span>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          {metaOpen && isAdmin && !isAdminRole && activeRole && (
            <div data-testid="role-active-editor" className={`rounded-md p-4 ring-1 ring-inset ${activeRole.disabled ? "bg-amber-50 ring-amber-200" : "bg-white ring-slate-200"}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="flex items-center gap-1.5 font-heading text-sm font-bold text-slate-800"><Power className="h-4 w-4 text-slate-500" /> Rol Durumu (Budama)</p>
                  <p className="text-[11px] text-slate-400">Pasif rol, kullanıcı atama listelerinden gizlenir; kod ve onay zinciri korunur (geri alınabilir). Bu rolde <b>{activeRole.user_count || 0}</b> kullanıcı var.</p>
                </div>
                {activeRole.disabled
                  ? <Button data-testid="role-activate-btn" onClick={() => doToggleActive(true)} disabled={saving} size="sm" className="bg-brand-green text-white hover:bg-brand-green-600"><Power className="mr-1.5 h-4 w-4" /> Aktifleştir</Button>
                  : <Button data-testid="role-deactivate-btn" onClick={() => doToggleActive(false)} disabled={saving} variant="outline" size="sm" className="border-amber-200 text-amber-700 hover:bg-amber-50" title={(activeRole.user_count || 0) > 0 ? "Önce bu roldeki kullanıcıların rolünü değiştirin" : ""}><Power className="mr-1.5 h-4 w-4" /> Pasifleştir</Button>}
              </div>
              {activeRole.disabled && <p data-testid="role-disabled-note" className="mt-2 rounded-md bg-white/70 px-2 py-1 text-[11px] font-semibold text-amber-700">Bu rol şu an PASİF — yeni kullanıcı atamalarında görünmez.</p>}
            </div>
          )}
          {/* Amir zinciri artık yalnız üstteki "Onay Zinciri Haritası"ndan yönetilir (tek kaynak) */}
          {/* Özel rol: ad + sil */}
          {metaOpen && isCustom && (
            <div data-testid="role-meta-editor" className="rounded-md bg-white p-4 ring-1 ring-inset ring-brand-orange/30">
              <div className="mb-3 flex items-center justify-between">
                <p className="flex items-center gap-1.5 font-heading text-sm font-bold text-slate-800"><Sparkles className="h-4 w-4 text-brand-orange" /> Özel Rol</p>
                <Button data-testid="role-delete-btn" onClick={askDelete} variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50">
                  <Trash2 className="mr-1.5 h-4 w-4" /> Rolü Sil
                </Button>
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[200px]"><Label>Rol Adı</Label><Input data-testid="role-meta-label" value={metaLabel} onChange={(e) => setMetaLabel(e.target.value)} className="mt-1" /></div>
                <Button data-testid="role-meta-save" onClick={() => run(doSaveMeta)} disabled={saving} size="sm" className="bg-brand-green text-white hover:bg-brand-green-600">
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Adı Kaydet
                </Button>
              </div>
            </div>
          )}

          {/* Bu Rol Ne Yapabilir? — okunur yetenek özeti */}
          {capOpen && !isAdminRole && activeRole && (
            <div data-testid="role-capability-summary" className="rounded-md bg-emerald-50/60 p-4 ring-1 ring-inset ring-emerald-200">
              <p className="flex items-center gap-1.5 font-heading text-sm font-bold text-slate-800"><ShieldCheck className="h-4 w-4 text-brand-green" /> Bu Rol Ne Yapabilir? — {metaLabel}</p>
              <p className="mt-0.5 text-[11px] text-slate-500"><b>Yetkiler</b> = kullanıcının yapabildiği işlemler. <b>Modül Görünürlüğü</b> = menüde gördüğü ekranlar (bir modülü görüp yine de düzenleyememesi mümkün). Aşağıdaki şerit seçili yetkileri özetler.</p>
              {draft.size === 0 ? (
                <p className="mt-2 text-sm text-slate-400">Henüz hiçbir yetki seçili değil — bu rol yalnızca giriş yapabilir.</p>
              ) : (
                <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {Object.entries(groups).map(([group, items]) => {
                    const granted = items.filter((p) => draft.has(p.code));
                    if (granted.length === 0) return null;
                    return (
                      <div key={group} className="rounded-md bg-white p-2.5 ring-1 ring-inset ring-slate-200">
                        <p className="overline mb-1">{group}</p>
                        <div className="flex flex-wrap gap-1">
                          {granted.map((p) => <span key={p.code} className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800">{p.label}</span>)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Birleşik yetki ağacı — Modül → İşlem → Alt-sekme (otomatik kayıt) */}
          {isAdminRole ? (
            <div className="rounded-md bg-white p-8 text-center ring-1 ring-inset ring-slate-200">
              <ShieldCheck className="mx-auto mb-2 h-10 w-10 text-brand-green" />
              <p className="text-sm text-slate-500">Yönetici (Owner) tüm modüllere sınırsız erişebilir ve hiyerarşinin en üstündedir. Yetkileri değiştirilemez.</p>
            </div>
          ) : activeRole && (
            <div data-testid="role-perm-tree-wrap" className="rounded-md bg-white p-3 ring-1 ring-inset ring-slate-200">
              <p className="mb-2.5 text-[11px] text-slate-400"><b className="text-sky-600">Menüde</b> = modül sol menüde görünür · <b className="text-emerald-700">İşlemler</b> = yapabildiği eylemler · <b className="text-sky-700">Alt-sekmeler</b> = modül içi sekmeler. Modül/alt-sekme görünürlüğü yalnızca Yönetici tarafından değiştirilir.</p>
              <PermissionTree tree={tree} query={treeQuery}
                renderModuleVis={(m) => {
                  const codes = (m.perms || []).map((p) => p.code);
                  const allOn = codes.length > 0 && codes.every((c) => draft.has(c));
                  const slug = m.key === "/" ? "panel" : m.key.slice(1);
                  return (
                    <div className="flex items-center gap-2">
                      {codes.length > 0 && (
                        <button type="button" data-testid={`role-modall-${slug}`} onClick={(e) => { e.stopPropagation(); toggleAllModulePerms(m); }}
                          className={`rounded-md px-2 py-1 text-[10px] font-bold ring-1 ring-inset transition-colors ${allOn ? "bg-emerald-500 text-white ring-emerald-500" : "text-emerald-700 ring-emerald-200 hover:bg-emerald-50"}`}
                          title="Bu modülün tüm işlemlerini seç / kaldır">
                          {allOn ? "Tümü açık ✓" : "Tümünü seç"}
                        </button>
                      )}
                      <label className="flex items-center gap-1.5 text-[11px] font-semibold">
                        <span className={moduleHidden.has(m.key) ? "text-slate-400" : "text-sky-600"}>{moduleHidden.has(m.key) ? "Gizli" : "Menüde"}</span>
                        <Switch data-testid={`role-modvis-${slug}`} disabled={!isAdmin} checked={!moduleHidden.has(m.key)} onCheckedChange={() => toggleVisAuto(m.key)} />
                      </label>
                    </div>
                  );
                }}
                renderPerm={(p) => (
                  <label className="flex items-center justify-between gap-2 rounded-md bg-white px-2.5 py-1.5 ring-1 ring-inset ring-slate-100">
                    <span className="text-xs text-slate-600">{p.label}</span>
                    <Switch data-testid={`role-perm-${p.code}`} checked={draft.has(p.code)} onCheckedChange={() => togglePermAuto(p.code)} />
                  </label>
                )}
                renderSubtab={(s) => (
                  <label className="flex items-center justify-between gap-2 rounded-md bg-white px-2.5 py-1.5 ring-1 ring-inset ring-slate-100">
                    <span className="text-xs text-slate-600">{s.label}</span>
                    <Switch data-testid={`role-subtab-${s.key.replace(/[/#]+/g, "-").replace(/^-+/, "")}`} disabled={!isAdmin} checked={!moduleHidden.has(s.key)} onCheckedChange={() => toggleVisAuto(s.key)} />
                  </label>
                )}
              />
            </div>
          )}
        </div>
      </div>

      {/* Create role dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="role-create-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><Sparkles className="h-5 w-5 text-brand-orange" /> Yeni Rol</DialogTitle>
            <DialogDescription>Rolü oluşturun; ardından yetkilerini ve amir(ler)ini bu ekrandan ayarlayın.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Rol Adı *</Label><Input data-testid="role-create-label" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} className="mt-1" placeholder="Örn. Reyon Personeli" /></div>
            <div>
              <Label>Başlangıç Amiri (sonra çoklu düzenlenebilir)</Label>
              <select data-testid="role-create-superior" value={newSup} onChange={(e) => setNewSup(e.target.value)}
                className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                {matrix.map((r) => <option key={r.role} value={r.role}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Şablon (yetkileri kopyala)</Label>
              <select data-testid="role-create-template" value={newTemplate} onChange={(e) => setNewTemplate(e.target.value)}
                className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                <option value="">Boş rol (yetki yok)</option>
                <optgroup label="Mevcut roller">
                  {matrix.filter((r) => r.role !== "admin").map((r) => <option key={r.role} value={r.role}>{r.label} benzeri</option>)}
                </optgroup>
                {templates.length > 0 && (
                  <optgroup label="Kayıtlı şablonlar">
                    {templates.map((t) => <option key={t.id} value={`tpl:${t.id}`}>{t.name} ({(t.permissions || []).length} yetki)</option>)}
                  </optgroup>
                )}
              </select>
              <p className="mt-1 text-[11px] text-slate-400">Seçersen o rolün tüm yetkileriyle başlar; ardından bu ekrandan düzenleyebilirsin.</p>
            </div>
            <Button data-testid="role-create-submit" onClick={() => (isAdmin ? doCreate(null) : setPending(() => doCreate))} disabled={creating} className="w-full bg-brand-green text-white hover:bg-brand-green-600">
              {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Rolü Oluştur
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ApprovalDialog open={!!pending} onClose={() => setPending(null)} permission="approve.admin"
        title="Yönetici Onayı Gerekli" description="Rol değişikliği için Yönetici onayı gerekiyor."
        onApproved={(cred) => { if (pending) pending(cred); setPending(null); }} />
    </div>
  );
}


function ChainMap({ matrix, isAdmin, perms, onEditSuperiors, onAddSubordinate, onSelectRole }) {
  const [open, setOpen] = useState(false);
  const [panelRole, setPanelRole] = useState(null);
  const [draggedRole, setDraggedRole] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [hoverRole, setHoverRole] = useState(null);
  const mapRef = useRef(null);
  const nodeRefs = useRef({});
  const [lines, setLines] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [pendingByRole, setPendingByRole] = useState({});
  useEffect(() => {
    api.get("/approvals/summary").then((r) => {
      const m = {}; (r.data?.by_approver_role || []).forEach((x) => { m[x.label] = x.count; }); setPendingByRole(m);
    }).catch(() => {});
  }, []);
  useLayoutEffect(() => {
    if (!open) { setLines([]); return undefined; }
    const compute = () => {
      const cont = mapRef.current; if (!cont) return;
      const cb = cont.getBoundingClientRect();
      const segs = [];
      (matrix || []).forEach((r) => {
        const childEl = nodeRefs.current[r.role]; if (!childEl) return;
        (r.superiors || []).forEach((s) => {
          const supEl = nodeRefs.current[s.code]; if (!supEl) return;
          const c = childEl.getBoundingClientRect();
          const p = supEl.getBoundingClientRect();
          segs.push({ key: `${r.role}-${s.code}`, child: r.role, sup: s.code,
            x1: p.left + p.width / 2 - cb.left, y1: p.bottom - cb.top,
            x2: c.left + c.width / 2 - cb.left, y2: c.top - cb.top });
        });
      });
      setLines(segs);
    };
    const raf = requestAnimationFrame(compute);
    const t = setTimeout(compute, 250);
    window.addEventListener("resize", compute);
    return () => { cancelAnimationFrame(raf); clearTimeout(t); window.removeEventListener("resize", compute); };
  }, [open, matrix]);
  if (!matrix || !matrix.length) return null;
  const byRole = Object.fromEntries(matrix.map((r) => [r.role, r]));
  const levelOf = {};
  const calc = (code, seen) => {
    if (levelOf[code] != null) return levelOf[code];
    if (seen.has(code)) return 0;
    seen.add(code);
    const r = byRole[code];
    const sups = (r?.superiors || []).map((s) => s.code).filter((c) => byRole[c]);
    if (!sups.length) { levelOf[code] = 0; return 0; }
    const lv = 1 + Math.max(...sups.map((s) => calc(s, seen)));
    levelOf[code] = lv;
    return lv;
  };
  matrix.forEach((r) => calc(r.role, new Set()));
  const maxLv = Math.max(0, ...Object.values(levelOf));
  const levels = [];
  for (let i = 0; i <= maxLv; i++) levels.push(matrix.filter((r) => levelOf[r.role] === i));
  const openPanel = (r) => { setSel(new Set((r.superiors || []).map((s) => s.code))); setPanelRole(r); };
  // Sürükle-bırak: hedefe (target) çıkan zincir dragged'a ulaşıyorsa döngü olur → engelle.
  const reachesUp = (from, target, seen = new Set()) => {
    if (from === target) return true;
    if (seen.has(from)) return false;
    seen.add(from);
    return ((byRole[from]?.superiors || []).map((s) => s.code)).some((s) => reachesUp(s, target, seen));
  };
  const onDropNode = (targetRole) => {
    const d = draggedRole; setDraggedRole(null); setDragOver(null);
    if (!d || d === targetRole) return;
    if (d === "admin") { toast.error("Yönetici en üsttedir; amiri olamaz."); return; }
    if (reachesUp(targetRole, d)) { toast.error("Döngü oluşturulamaz — bir rol kendi alt rütbesine bağlanamaz."); return; }
    onEditSuperiors?.(d, [targetRole]);
  };
  const toggleSel = (code) => setSel((s) => { const n = new Set(s); n.has(code) ? n.delete(code) : n.add(code); return n; });
  const saveAmir = () => { if (!panelRole) return; onEditSuperiors?.(panelRole.role, Array.from(sel)); setPanelRole(null); };
  const pr = panelRole;
  const grantedCodes = pr ? (pr.permissions || []) : [];
  const isAllPerms = grantedCodes.includes("*");
  const canEditAmir = pr && pr.role !== "admin";
  return (
    <div className="rounded-md bg-white ring-1 ring-inset ring-indigo-300/40" data-testid="chain-map-card">
      <button data-testid="chain-map-toggle" onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-4 py-3 text-left">
        <span className="flex items-center gap-2">
          <GitBranch className="h-4 w-4 text-indigo-600" />
          <span className="font-heading text-sm font-bold text-slate-800">Onay Zinciri & Yetki Haritası</span>
          <span className="hidden text-xs text-slate-400 sm:inline">— amir akışı · düğüme tıkla: amiri düzenle</span>
        </span>
        <span className="text-xs font-semibold text-indigo-600">{open ? "Gizle" : "Göster"}</span>
      </button>
      {open && (
        <div className="border-t border-slate-100 p-4">
          <p className="mb-4 text-[11px] text-slate-400">Bir işlem onaya düştüğünde <b>alttan üste</b> doğru amire iletilir. En üstte <b>Yönetici</b> vardır. Bir role <b>tıklayınca</b> küçük bir pencerede o rolün <b>amiri</b> düzenlenir. Bir rolü <b>sürükleyip</b> başka rolün üzerine bırakarak amirini hızlıca değiştirebilirsiniz. Boş düğümdeki <b>"+ Alt rütbe ekle"</b> ile o role bağlı yeni rol oluşturabilirsiniz; yetkiler aşağıdaki rol düzenleyicisinden aç/kapa yapılır. Fareyle bir rolün üzerine gelince yalnızca o rolün onay zinciri vurgulanır.</p>
          <div ref={mapRef} className="relative space-y-1">
            <svg className="pointer-events-none absolute inset-0 h-full w-full" style={{ zIndex: 0 }}>
              {lines.map((l) => {
                const active = hoverRole && (l.child === hoverRole || l.sup === hoverRole);
                const dim = hoverRole && !active;
                const midY = (l.y1 + l.y2) / 2;
                return (
                  <path key={l.key} d={`M ${l.x1} ${l.y1} C ${l.x1} ${midY}, ${l.x2} ${midY}, ${l.x2} ${l.y2}`}
                    fill="none" stroke={active ? "#6366f1" : "#cbd5e1"} strokeWidth={active ? 2.5 : 1.5}
                    strokeOpacity={dim ? 0.12 : active ? 1 : 0.5} className="transition-all" />
                );
              })}
            </svg>
            {levels.map((lvRoles, li) => (
              <div key={li}>
                <div className="flex flex-wrap items-stretch justify-center gap-2" data-testid={`chain-level-${li}`}>
                  {lvRoles.map((r) => (
                    <div key={r.role} data-testid={`chain-node-${r.role}`}
                      ref={(el) => { if (el) nodeRefs.current[r.role] = el; }}
                      onClick={() => openPanel(r)} role="button"
                      onMouseEnter={() => setHoverRole(r.role)} onMouseLeave={() => setHoverRole(null)}
                      draggable={r.role !== "admin"}
                      onDragStart={(e) => { setDraggedRole(r.role); e.dataTransfer.effectAllowed = "move"; }}
                      onDragEnd={() => { setDraggedRole(null); setDragOver(null); }}
                      onDragOver={(e) => { if (draggedRole && draggedRole !== r.role) { e.preventDefault(); setDragOver(r.role); } }}
                      onDragLeave={() => setDragOver((x) => (x === r.role ? null : x))}
                      onDrop={(e) => { e.preventDefault(); onDropNode(r.role); }}
                      className={`group relative z-10 min-w-[130px] ${r.role === "admin" ? "cursor-pointer" : "cursor-grab active:cursor-grabbing"} rounded-lg px-3 py-2 text-center ring-1 ring-inset transition-all hover:ring-indigo-400 hover:shadow-sm ${li === 0 ? "bg-amber-50 ring-amber-300" : "bg-slate-50 ring-slate-200"} ${r.disabled ? "opacity-40" : ""} ${draggedRole === r.role ? "opacity-50" : ""} ${dragOver === r.role ? "ring-2 ring-emerald-500 shadow-md" : ""}`}>
                      <Pencil className="absolute right-1.5 top-1.5 h-3 w-3 text-slate-300 opacity-0 transition-opacity group-hover:opacity-100" />
                      <div className="flex items-center justify-center gap-1 text-[12px] font-bold text-slate-800">
                        {r.role === "admin" && <ShieldCheck className="h-3.5 w-3.5 text-amber-500" />}
                        {!r.builtin && <Sparkles className="h-3 w-3 text-brand-orange" />}
                        {r.label}
                      </div>
                      <div className="mt-0.5 text-[10px] text-slate-400">{r.user_count || 0} kullanıcı{(r.subordinates || []).length ? ` · ${r.subordinates.length} alt rütbe` : ""}</div>
                      {(pendingByRole[r.label] || 0) > 0 && (
                        <div className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-rose-50 px-1.5 py-0.5 text-[9px] font-semibold text-rose-600" data-testid={`chain-node-pending-${r.role}`}>
                          <Inbox className="h-2.5 w-2.5" /> {pendingByRole[r.label]} bekleyen
                        </div>
                      )}
                      {li > 0 && (r.superiors || []).length > 0 && (
                        <div className="mt-1 flex flex-wrap justify-center gap-1">
                          {(r.superiors || []).map((s) => (
                            <span key={s.code} className="rounded-full bg-indigo-50 px-1.5 py-0.5 text-[9px] font-semibold text-indigo-600">↑ {s.label}</span>
                          ))}
                        </div>
                      )}
                      {onAddSubordinate && (
                        <div className="mt-1.5 flex justify-center">
                          <button data-testid={`chain-add-sub-${r.role}`}
                            onClick={(e) => { e.stopPropagation(); onAddSubordinate(r.role); }}
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200 transition-colors hover:bg-emerald-100">
                            <Plus className="h-2.5 w-2.5" /> Alt rütbe ekle
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {li < levels.length - 1 && <div className="flex justify-center py-1"><ChevronDown className="h-4 w-4 text-slate-300" /></div>}
              </div>
            ))}
          </div>
        </div>
      )}
      <Dialog open={!!panelRole} onOpenChange={(o) => !o && setPanelRole(null)}>
        <DialogContent data-testid="chain-node-panel" className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              {pr?.role === "admin" && <ShieldCheck className="h-5 w-5 text-amber-500" />}
              {pr && !pr.builtin && <Sparkles className="h-4 w-4 text-brand-orange" />}
              {pr?.label}
            </DialogTitle>
            <DialogDescription>
              {pr ? `${pr.user_count || 0} kullanıcı · ${isAllPerms ? "tüm" : grantedCodes.length} yetki — ` : ""}amiri seçin; onaylar bu rollere iletilir.
            </DialogDescription>
          </DialogHeader>
          {pr && (canEditAmir ? (
            <div className="space-y-3" data-testid="panel-superiors">
              <div className="grid max-h-[46vh] grid-cols-2 gap-1.5 overflow-y-auto">
                {matrix.filter((x) => x.role !== pr.role).map((x) => (
                  <label key={x.role} data-testid={`chain-sup-opt-${x.role}`}
                    className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm ring-1 ring-inset ring-slate-100 hover:bg-slate-50">
                    <input type="checkbox" checked={sel.has(x.role)} onChange={() => toggleSel(x.role)} className="h-4 w-4 accent-indigo-600" />
                    <span className="truncate text-slate-700">{x.label}</span>
                  </label>
                ))}
              </div>
              <Button data-testid="chain-edit-save" onClick={saveAmir} className="w-full bg-indigo-600 text-white hover:bg-indigo-700">
                <Save className="mr-2 h-4 w-4" /> Kaydet
              </Button>
            </div>
          ) : (
            <p className="rounded-md bg-amber-50 px-3 py-2.5 text-[12px] text-amber-700 ring-1 ring-inset ring-amber-200" data-testid="panel-superiors-admin">
              Bu rol hiyerarşinin en üstündedir — amiri yoktur.
            </p>
          ))}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UserDiscountGrantsCard({ isAdmin }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [grants, setGrants] = useState([]);
  const [selEmail, setSelEmail] = useState("");
  const [maxPct, setMaxPct] = useState("");
  const [weekly, setWeekly] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/pos/discount/user-grants");
      setUsers(r.data.users || []);
      setGrants(r.data.grants || []);
    } catch (err) { /* yetki yoksa sessiz */ }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open && users.length === 0) load(); }, [open]); // eslint-disable-line

  const addRow = () => {
    if (!selEmail) return toast.error("Kullanıcı seçin");
    const pct = Number(maxPct);
    if (!pct || pct <= 0) return toast.error("Geçerli bir maksimum oran girin (%0'dan büyük)");
    const u = users.find((x) => x.email === selEmail);
    if (!u) return;
    setGrants((g) => [...g.filter((x) => x.email !== selEmail),
      { email: u.email, name: u.name, role_label: u.role_label, max_pct: pct, weekly_limit: Number(weekly) || 0 }]);
    setSelEmail(""); setMaxPct(""); setWeekly("");
  };
  const removeRow = (email) => setGrants((g) => g.filter((x) => x.email !== email));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      grants.forEach((x) => { payload[x.email] = { max_pct: Number(x.max_pct), weekly_limit: Number(x.weekly_limit) || 0 }; });
      await api.put("/pos/discount/user-grants", { grants: payload });
      toast.success("Kullanıcı bazlı iskonto yetkileri kaydedildi");
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Kaydedilemedi (yalnızca Yönetici)"); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-md bg-white ring-1 ring-inset ring-sky-300/40" data-testid="user-discount-card">
      <button data-testid="user-discount-toggle" onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-4 py-3 text-left">
        <span className="flex items-center gap-2">
          <Percent className="h-4 w-4 text-sky-600" />
          <span className="font-heading text-sm font-bold text-slate-800">Kullanıcı Bazlı İskonto Yetkisi</span>
          <span className="text-xs text-slate-400">— rolden bağımsız, seçtiğiniz kişiye iskonto hakkı</span>
        </span>
        <span className="text-xs font-semibold text-sky-600">{open ? "Gizle" : "Düzenle"}</span>
      </button>
      {open && (
        <div className="border-t border-slate-100 p-4">
          {loading ? (
            <p className="py-4 text-center text-sm text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></p>
          ) : (
            <>
              <p className="mb-3 text-[11px] text-slate-400">Bir kullanıcı seçin, uygulayabileceği <b>maksimum oranı</b> ve <b>haftalık hak</b> sayısını yazın. Bu yetki, kullanıcının rol standardının <b>önüne geçer</b> (rolünde iskonto hakkı olmasa bile verir).</p>
              {isAdmin && (
                <div className="mb-3 flex flex-wrap items-end gap-2 rounded-md bg-slate-50 p-3">
                  <div className="min-w-[180px] flex-1">
                    <label className="overline">Kullanıcı</label>
                    <select data-testid="user-discount-select" value={selEmail} onChange={(e) => setSelEmail(e.target.value)}
                      className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
                      <option value="">— seçin —</option>
                      {users.map((u) => <option key={u.email} value={u.email}>{`${u.name} · ${u.role_label}`}</option>)}
                    </select>
                  </div>
                  <div className="w-24">
                    <label className="overline">Maks. %</label>
                    <input data-testid="user-discount-max" type="number" min="0" max="100" step="0.5" value={maxPct} onChange={(e) => setMaxPct(e.target.value)}
                      className="mt-1 h-9 w-full rounded-md border border-input bg-white px-2 text-right tabular" placeholder="10" />
                  </div>
                  <div className="w-28">
                    <label className="overline">Haftalık Hak</label>
                    <input data-testid="user-discount-week" type="number" min="0" step="1" value={weekly} onChange={(e) => setWeekly(e.target.value)}
                      className="mt-1 h-9 w-full rounded-md border border-input bg-white px-2 text-right tabular" placeholder="4" />
                  </div>
                  <Button data-testid="user-discount-add" onClick={addRow} size="sm" className="bg-sky-600 text-white hover:bg-sky-700"><Plus className="mr-1 h-4 w-4" /> Ekle</Button>
                </div>
              )}
              {grants.length === 0 ? (
                <p className="py-3 text-center text-sm text-slate-400">Henüz kullanıcı bazlı iskonto yetkisi tanımlı değil.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
                        <th className="py-2 pr-3">Kullanıcı</th><th className="py-2 pr-3">Rol</th>
                        <th className="py-2 pr-3">Maks. %</th><th className="py-2 pr-3">Haftalık</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {grants.map((x) => (
                        <tr key={x.email} data-testid={`user-discount-row-${x.email}`} className="border-b border-slate-50">
                          <td className="py-2 pr-3 font-medium text-slate-700">{x.name}</td>
                          <td className="py-2 pr-3 text-slate-500">{x.role_label}</td>
                          <td className="py-2 pr-3 tabular font-semibold text-sky-700">%{x.max_pct}</td>
                          <td className="py-2 pr-3 tabular">{x.weekly_limit}</td>
                          <td className="py-2 text-right">
                            {isAdmin && <button data-testid={`user-discount-remove-${x.email}`} onClick={() => removeRow(x.email)} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {isAdmin && (
                <div className="mt-3 flex justify-end">
                  <Button data-testid="user-discount-save" onClick={save} disabled={saving} size="sm" className="bg-sky-600 text-white hover:bg-sky-700">
                    {saving ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Kaydediliyor…</> : <><Save className="mr-1.5 h-4 w-4" /> Yetkileri Kaydet</>}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

